import { useCallback } from 'react';

import styled from '@emotion/styled';

import { Button } from './Button';

export type PaginationProps = {
  className?: string;
  pageCount: number;
  pageIndex: number;
  onPageIndexChange: (pageIndex: number) => void;
};

const StyledPagination = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  gap: 24px;

  button {
    text-decoration-line: none;
    font-weight: 400;
  }

  .center-pagination {
    align-items: center;
    display: flex;
    justify-content: center;
    gap: 12px;
    flex-grow: 1;

    button {
      text-decoration-line: underline;
    }

    .active {
      cursor: default;
      font-weight: bold;
      font-weight: 700;
      text-decoration-line: none;
    }

    .pages {
      align-items: center;
      display: flex;
      justify-content: center;
      gap: 18px;
    }
  }
`;

export const Pagination = ({ className, pageCount, pageIndex, onPageIndexChange }: PaginationProps) => {
  const canGoNextPage = pageIndex < pageCount - 1;
  const canGoPreviousPage = pageIndex > 0;

  const goToPreviousPage = useCallback(() => {
    onPageIndexChange(pageIndex - 1);
  }, [onPageIndexChange, pageIndex]);

  const goToNextPage = useCallback(() => {
    onPageIndexChange(pageIndex + 1);
  }, [onPageIndexChange, pageIndex]);

  if (pageCount === 1) {
    return null;
  }

  return (
    <StyledPagination className={className}>
      {pageCount > 1 && (
        <Button
          variant="discrete"
          onClick={goToPreviousPage}
          disabled={!canGoPreviousPage}
          text="Previous"
          icon="FiChevronLeft"
          textPosition="right"
        />
      )}
      <div className="center-pagination">
        <div className="pages">
          {pageCount <= 5 && (
            <>
              {Array(pageCount)
                .fill(null)
                .map((_, index) => (
                  <Button
                    variant="discrete"
                    className={pageIndex === index ? 'active' : ''}
                    key={index}
                    onClick={() => onPageIndexChange(index)}
                    text={index + 1}
                  />
                ))}
            </>
          )}
          {pageCount > 5 && (
            <>
              <Button
                variant="discrete"
                className={pageIndex === 0 ? 'active' : ''}
                onClick={() => onPageIndexChange(0)}
                text="1"
              />
              {pageIndex > 2 && <span>&hellip;</span>}
              {pageIndex > 1 && <Button variant="discrete" onClick={goToPreviousPage} text={pageIndex}></Button>}
              {pageIndex > 0 && pageIndex < pageCount - 1 && (
                <Button variant="discrete" className="active" text={pageIndex + 1} />
              )}
              {pageIndex < pageCount - 2 && <Button variant="discrete" onClick={goToNextPage} text={pageIndex + 2} />}
              {pageIndex < pageCount - 3 && <span>&hellip;</span>}
              <Button
                variant="discrete"
                className={pageIndex === pageCount - 1 ? 'active' : ''}
                onClick={() => onPageIndexChange(pageCount - 1)}
                text={pageCount}
              />
            </>
          )}
        </div>
      </div>
      {pageCount > 1 && (
        <Button variant="discrete" onClick={goToNextPage} disabled={!canGoNextPage} text="Next" icon="FiChevronRight" />
      )}
    </StyledPagination>
  );
};
